// Dates in contracts and what each one is: when the contract starts, when it ends, when it was
// signed, or something else (a cheque date, an Act's year, a payment). Also the term written as a
// duration ("for a period of eleven (11) months commencing from …") when no end date is given.
import { addDays, addMonths, isValidIsoDate } from "../dates";
import { DURATION, NUM_WORDS, parseNumberWords, quantity, toMonths, unitOf } from "./numbers";

export type DateOrder = "dmy" | "mdy";

export const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6,
  jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};
const MONTH_NAMES = Object.keys(MONTHS).sort((a, b) => b.length - a.length).join("|");

export interface FoundDate {
  date: string;
  index: number;
  text: string;
}

const pad = (n: number) => String(n).padStart(2, "0");

function iso(y: number, m: number, d: number): string | null {
  if (y < 100) y += 2000;
  const s = `${y}-${pad(m)}-${pad(d)}`;
  return isValidIsoDate(s) ? s : null;
}

/** "two thousand twenty six" / "two thousand and twenty-six" → 2026. */
function yearWords(s: string): number | null {
  const n = parseNumberWords(s.replace(/,/g, " "));
  return n && n >= 1900 && n <= 2200 ? n : null;
}

const ORDINAL_WORDS = `(?:(?:twenty|thirty)[\\s-]+)?(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth|thirtieth)`;

/** Every date in the text, in the formats contracts use. `text` should have collapsed whitespace. */
export function findDates(text: string, order: DateOrder = "dmy"): FoundDate[] {
  const found: FoundDate[] = [];
  const patterns: { re: RegExp; parse: (m: RegExpExecArray) => string | null }[] = [
    // 2026-01-31, 2026/01/31
    { re: /\b(\d{4})([-/])(\d{1,2})\2(\d{1,2})\b/g, parse: (m) => iso(+m[1]!, +m[3]!, +m[4]!) },
    // 31 January 2026, 31st of Jan. 2026, 1st day of March, 2026, 21-Jun-2026, 01-Jan-26
    {
      re: new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?(?:\\s+(?:day\\s+)?(?:of\\s+)?|[-/.](?=\\p{L}))(${MONTH_NAMES})\\.?(?:,?\\s+|[-/.])(\\d{4}|\\d{2}(?![\\d:]))\\b`, "giu"),
      parse: (m) => iso(+m[3]!, MONTHS[m[2]!.toLowerCase()]!, +m[1]!),
    },
    // January 31, 2026 / Jan 31st 2026
    {
      re: new RegExp(`\\b(${MONTH_NAMES})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})\\b`, "gi"),
      parse: (m) => iso(+m[3]!, MONTHS[m[1]!.toLowerCase()]!, +m[2]!),
    },
    // 31/01/2026, 01-31-2026, 31.01.2026, 31/01/26 (two-digit years only with slashes; "5.1.26" is often a clause number)
    {
      re: /\b(\d{1,2})(?:([/.-])(\d{1,2})\2(\d{4})|\/(\d{1,2})\/(\d{2}))\b/g,
      parse: (m) => {
        const a = +m[1]!;
        const b = +(m[3] ?? m[5])!;
        const y = +(m[4] ?? m[6])!;
        if (a > 12) return iso(y, b, a);
        if (b > 12) return iso(y, a, b);
        return order === "mdy" ? iso(y, a, b) : iso(y, b, a);
      },
    },
    // the twenty-fifth day of March, Two Thousand Twenty Six (deeds)
    {
      re: new RegExp(`\\b(${ORDINAL_WORDS})\\s+day\\s+of\\s+(${MONTH_NAMES}),?\\s+(?:(\\d{4})|(?:in\\s+the\\s+year\\s+)?(two\\s+thousand(?:[\\s-]+(?:and\\s+)?${NUM_WORDS})?))\\b`, "gi"),
      parse: (m) => {
        const d = parseNumberWords(m[1]!);
        const y = m[3] ? +m[3] : yearWords(m[4]!);
        return d && y ? iso(y, MONTHS[m[2]!.toLowerCase()]!, d) : null;
      },
    },
  ];
  for (const { re, parse } of patterns) {
    for (const m of text.matchAll(re)) {
      const date = parse(m as RegExpExecArray);
      if (date && !found.some((f) => m.index! < f.index + f.text.length && f.index < m.index! + m[0].length)) found.push({ date, index: m.index!, text: m[0] });
    }
  }
  return found.sort((a, b) => a.index - b.index);
}

/**
 * Whether the document writes numeric dates day first or month first, from the document itself: a
 * date that can only be read one way ("30.06.2027"), then dotted dates (European and Indian), then
 * the currency it uses. `fallback` (from the reader's language) settles the rest.
 */
export function inferDateOrder(text: string, fallback: DateOrder): DateOrder {
  let dmy = 0;
  let mdy = 0;
  for (const m of text.matchAll(/\b(\d{1,2})([/.-])(\d{1,2})\2(\d{4}|\d{2})\b/g)) {
    if (+m[1]! > 12 && +m[3]! <= 12) dmy++;
    else if (+m[3]! > 12 && +m[1]! <= 12) mdy++;
  }
  if (dmy && !mdy) return "dmy";
  if (mdy && !dmy) return "mdy";
  if (!dmy && !mdy && /\b\d{1,2}\.\d{1,2}\.\d{4}\b/.test(text)) return "dmy";
  if (/₹|\bRs\b|\bINR\b|\brupees?\b|\bGST\b|£|\bGBP\b|€|\bEUR\b|\bAUD\b|\bNZD\b/i.test(text)) return "dmy";
  if (/\bUSD\b|US\$|\$\s?\d/.test(text) && !/\bCAD\b|\bAUD\b|\bSGD\b|\bNZD\b/.test(text)) return "mdy";
  return fallback;
}

export type DateRole = "start" | "end" | "signed" | "joining" | "first_emi" | "last_emi" | "other";

export interface RoledDate extends FoundDate {
  role: DateRole;
  /** 4 = labelled ("Start Date:", "(the "Effective Date")") or a stated range, 3 = clause cue, 2 = weaker cue, 1 = only a nearby word. */
  strength: number;
  /** The document defines this date as "the Effective Date". */
  effective?: boolean;
}

// Labelled fields in schedules and order forms ("Subscription Start Date:", "Expiry Date:").
const LABEL_START = /(?:^|[\s(])(?:(?:(?:subscription|contract|policy|membership|agreement|lease|licen[cs]e|plan|service|coverage|cover|risk)\s+)?(?:start(?:ing)?|commencement|effective|inception|activation|valid\s+from|from)\s+date|date\s+of\s+(?:risk\s+)?(?:commencement|inception|start))\s*[:\-–]?\s*$/i;
const LABEL_END = /(?:^|[\s(])(?:(?:(?:subscription|contract|policy|membership|agreement|lease|licen[cs]e|plan|service|coverage|cover|risk)\s+)?(?:end(?:ing)?|expiry|expiration|termination|renewal|maturity|valid\s+(?:till|until|upto|up\s+to))\s+date|date\s+of\s+(?:maturity|expiry|expiration))\s*[:\-–]?\s*$|valid\s+(?:till|until|upto|up\s+to)\s*[:\-–]?\s*$/i;
const LABEL_SIGNED = /(?:^|[\s(])(?:(?:po|order|letter|invoice|agreement|contract)\s+)?date(?:d)?\s*[:\-–]\s*$|(?:po|order|letter|issue)\s+date\s*[:\-–]?\s*$/i;
// Clause cues anywhere earlier in the same clause.
const STRONG_START = /\b(?:effective(?:\s+(?:from|as\s+of|on))?|commenc\w*|start\s+date|starts?\s+on|starting(?:\s+(?:on|from))?|begin\w*|w\.?e\.?f\.?|with\s+effect\s+from|from\s+and\s+including|period\s+of\s+(?:insurance|cover)\s*:?\s*from)\b/gi;
const EFFECTIVE_CUE = /\beffective(?:\s+(?:from|as\s+of|on))?\b/gi;
// "The Subscription Term begins on", "This engagement shall be for six months starting": when the term
// itself starts. That beats an effective date when a contract has both.
const TERM_SUBJECT = /\b(?:term|tenancy|subscription|lease|licen[cs]e|engagement|internship|employment|membership|agreement|contract|appointment|services?|cover(?:age)?)\b/i;
const SIGNED = /\b(?:made\s+(?:and\s+(?:entered\s+into|executed)\s+)?(?:at\s+[\p{Lu}][\p{L}]+(?:\s+[\p{Lu}][\p{L}]+)?\s+)?(?:on|as\s+of)|entered\s+into(?:\s+(?:on|as\s+of))?|executed(?:\s+at\s+[\p{Lu}][\p{L}]+(?:\s+[\p{Lu}][\p{L}]+)?)?(?:\s+on)?|signed(?:\s+on)?|(?:is\s+)?dated|this\s+agreement\s+is\s+dated)\b/giu;
const STRONG_END = /\b(?:expir\w*|terminat\w*\s+(?:on|at)|end\s+date|ending\s+on|(?:shall|will)\s+end|conclud\w*|valid\s+(?:up\s*)?(?:to|till|until)|remain\s+valid\s+(?:till|until|up\s*to)|in\s+(?:full\s+)?force\s+(?:and\s+effect\s+)?(?:until|till|through|up\s+to))\b/gi;
const JOINING = /\b(?:date\s+of\s+joining|joining\s+date|(?:you\s+will\s+)?join(?:\s+us)?\s+on|employment\s+(?:will|shall)\s+(?:begin|commence|start)\s+on|start(?:ing)?\s+date\s+of\s+(?:your\s+)?employment)\b/gi;
const FIRST_EMI = /\bfirst\s+(?:emi|instal(?:l)?ment|repayment)\b/gi;
const LAST_EMI = /\b(?:last|final)\s+(?:emi|instal(?:l)?ment|repayment)\b/gi;
// Dates of other things: a cheque, a drawing, a notice, a stamp certificate, a previous agreement.
const OTHER = /\b(?:cheque|check|draft|dd|neft|rtgs|utr|receipt|boq|drawings?|quotation|proposal|invoice|letter|notice|resolution|certificate\s+issued|issued\s+date|birth|born|incorporat\w*|purchased|registration\s+date|manufactur\w*|previous|earlier|original)\b(?=[^.;]{0,40}$)/gi;
// Dots that don't end a clause ("w.e.f. 1 May", "Rs. 500", "No. 4"), dropped before splitting a clause at full stops.
const ABBREV_DOTS = /\b(?:w\.e\.f|i\.e|e\.g|viz|no|nos|rs|mr|mrs|ms|dr|sh|smt|shri|sri|st|co|inc|ltd|pvt|vs|h\.no|approx)\./gi;
// Weak cues count only right before the date, after filler like "on the" or "as of".
const FILLER = "(?:\\W+(?:on|from|as|of|the|day|is|be|shall|will|date|this|midnight|hrs|hours|\\d{1,2}[:.]\\d{2}))*\\W*$";
const WEAK_START = new RegExp(`\\b(from|with\\s+effect\\s+from|w\\.?e\\.?f\\.?)${FILLER}`, "i");
const WEAK_END = new RegExp(`\\b(ends?|ending|until|till|through|thru|to|upto|up\\s+to(?:\\s+and\\s+including)?)${FILLER}`, "i");
const DEFINED = /^\s*,?\s*\(\s*(?:the\s+|hereinafter[^")]{0,40})?["'“‘]+\s*(effective|commencement|start|expiration|expiry|termination|end|completion|execution)\s+date/i;
const RANGE_BETWEEN = /^\s*(?:\([^)]{0,30}\)\s*)?(?:to|till|until|upto|up\s+to|through|thru|-|–|—|and\s+ending\s+on|ending\s+on)\s*(?:midnight\s+of\s+|\d{1,2}[:.]\d{2}\s*(?:hrs|hours)?\s*(?:of|on)?\s*)?$/i;

function lastCue(clause: string, re: RegExp): number {
  let at = -1;
  for (const m of clause.matchAll(re)) at = Math.max(at, m.index! + m[0].length);
  return at;
}

/** What every date is, and how sure we are. `prevEnd` stops a cue reaching back past an earlier date. */
export function roleDates(text: string, dates: FoundDate[]): RoledDate[] {
  const out: RoledDate[] = dates.map((d, i) => {
    const prevEnd = i > 0 ? dates[i - 1]!.index + dates[i - 1]!.text.length : 0;
    const before = text
      .slice(Math.max(prevEnd, d.index - 140), d.index)
      .replace(/\b([ap])\.m\./gi, "$1m")
      .replace(ABBREV_DOTS, (w) => w.replace(/\./g, ""));
    const after = text.slice(d.index + d.text.length, d.index + d.text.length + 70);
    const defined = DEFINED.exec(after);
    if (defined) {
      const kind = defined[1]!.toLowerCase();
      if (kind === "execution") return { ...d, role: "signed", strength: 4 };
      const start = kind === "effective" || kind === "commencement" || kind === "start";
      return { ...d, role: start ? "start" : "end", strength: 4, effective: kind === "effective" };
    }
    // The label right before the date, on the same line of a schedule.
    const label = before.slice(-60);
    if (LABEL_START.test(label)) return { ...d, role: "start", strength: 4 };
    if (LABEL_END.test(label)) return { ...d, role: "end", strength: 4 };
    // Only this clause: nothing before the last sentence break.
    const clause = before.split(/[.;](?=\s)|:\s(?=[\p{Lu}\d])/u).pop() ?? "";
    const otherAt = lastCue(clause, OTHER);
    const cues: [number, DateRole, number][] = [
      [lastCue(clause, JOINING), "joining", 3],
      [lastCue(clause, FIRST_EMI), "first_emi", 3],
      [lastCue(clause, LAST_EMI), "last_emi", 3],
      [lastCue(clause, STRONG_START), "start", 3],
      [lastCue(clause, STRONG_END), "end", 3],
      [lastCue(clause, SIGNED), "signed", 3],
    ];
    const best = cues.filter((c) => c[0] >= 0).sort((a, b) => b[0] - a[0])[0];
    // "cheque dated 5 March" is the cheque's date; "Manufacturing division with effect from 1 May" isn't a manufacture date.
    if (otherAt >= 0 && !(best && best[1] !== "signed" && best[0] > otherAt)) return { ...d, role: "other", strength: 0 };
    if (best?.[1] === "start") {
      const effective = lastCue(clause, EFFECTIVE_CUE) === best[0];
      if (effective) return { ...d, role: "start", strength: 3, effective: true };
      return { ...d, role: "start", strength: TERM_SUBJECT.test(clause) ? 4 : 3 };
    }
    if (best) return { ...d, role: best[1], strength: best[2] };
    if (LABEL_SIGNED.test(label)) return { ...d, role: "signed", strength: 2 };
    if (WEAK_START.test(clause)) return { ...d, role: "start", strength: 1 };
    if (WEAK_END.test(clause)) return { ...d, role: "end", strength: 1 };
    // "this 5th day of March, 2026" opening a deed is when it was signed.
    if (/\bthis\s*$/i.test(clause) && /day\s+of/i.test(d.text)) return { ...d, role: "signed", strength: 2 };
    return { ...d, role: "other", strength: 0 };
  });
  // "01.07.2026 to 30.06.2027", "From 00:00 hrs of 15/04/2026 To Midnight of 14/04/2027": a stated range.
  for (let i = 0; i + 1 < out.length; i++) {
    const a = out[i]!;
    const b = out[i + 1]!;
    const between = text.slice(a.index + a.text.length, b.index);
    if (!RANGE_BETWEEN.test(between) || b.date <= a.date) continue;
    const lead = text.slice(Math.max(0, a.index - 80), a.index);
    const labelled = /\b(?:period|term|tenure|validity|valid|duration|insurance|cover(?:age)?|from|commencing|between)\b[^.;]*$/i.test(lead);
    const strength = labelled ? 4 : 2;
    if (a.role === "other" || a.role === "start" || a.strength < strength) Object.assign(a, { role: "start", strength: Math.max(a.strength, strength) });
    if (b.role === "other" || b.role === "end" || b.strength < strength) Object.assign(b, { role: "end", strength: Math.max(b.strength, strength) });
  }
  return out;
}

export interface Term {
  months: number;
  /** Where the term is stated, for evidence. */
  index: number;
  length: number;
  /** The term is counted from the date the document was signed ("from the date of signing"). */
  fromSigning: boolean;
}

// "for a period of eleven (11) months", "initial term of two (2) years", "Subscription Term: 12 months",
// "shall continue for a period of three years", "valid for a term of five (5) years".
// "This engagement shall be for six months", "Your internship will … last for three months".
const LASTS = "(?:(?:engagement|internship|appointment|arrangement|assignment|tenancy|lease|agreement|contract|membership)\\s+(?:shall|will)\\s+(?:be|last|run|continue)\\s+for\\s+|(?:will|shall)\\s+last\\s+(?:for\\s+)?)(?:a\\s+(?:period|term)\\s+of\\s+)?";
const TERM_RE = new RegExp(
  `\\b(?:(?:initial\\s+|subscription\\s+|contract\\s+|agreement\\s+|licen[cs]e\\s+|lease\\s+|policy\\s+)?(?:term|period|tenure|duration|validity|tenancy)` +
    `(?:\\s+of\\s+(?:this|the)\\s+[\\p{L}]+)?(?:\\s*[:\\-–]\\s*|\\s+(?:of|shall\\s+be|will\\s+be|is|be)\\s+(?:for\\s+)?(?:a\\s+(?:period|term)\\s+of\\s+)?)` +
    `|(?:continues?|remains?\\s+in\\s+(?:force|effect)|be\\s+valid|valid)\\s+(?:for\\s+(?:a\\s+(?:period|term)\\s+of\\s+)?|a\\s+(?:period|term)\\s+of\\s+)` +
    `|for\\s+a\\s+(?:period|term)\\s+of\\s+|${LASTS})${DURATION}`,
  "giu",
);
// Periods counted from when the contract ends ("twelve months after leaving") aren't its term.
const AFTER_END = /^\s*(?:\([^)]*\)\s*)?(?:after|following|post|from\s+(?:the\s+)?(?:date\s+of\s+)?(?:termination|expiry|expiration|cessation|leaving|resignation|the\s+end))\b/i;
const NOT_TERM = /\b(?:lock[\s-]?in|warrant\w*|probation\w*|confidential\w*|surviv\w*|notice|non[\s-]?(?:compete|solicit\w*)|restrict\w*|waiting|grace|free[\s-]look|cure|remed\w*|cooling|moratorium|defects?\s+liability|freez\w*|fit[\s-]?out|rent[\s-]free|retention|delay|after|following|thereafter|preceding|within|before|prior)\b/i;

/** The contract's term as a duration, if it states one. Lock-in, warranty, probation and survival periods don't count. */
export function findTerm(text: string): Term | null {
  for (const m of text.matchAll(TERM_RE)) {
    const lead = text.slice(Math.max(0, m.index! - 60), m.index! + m[0].length - m[1]!.length - m[2]!.length);
    // Only this clause or list item: "on the following terms: a) The tenancy is for …" starts at "a)".
    const clause = lead.split(/[.;:]\s|\s\(?[a-z\d]{1,3}\)\s/i).pop() ?? "";
    if (NOT_TERM.test(clause)) continue;
    if (AFTER_END.test(text.slice(m.index! + m[0].length, m.index! + m[0].length + 60))) continue;
    const n = quantity(m[1]!);
    if (!n || n <= 0) continue;
    const months = toMonths(n, unitOf(m[2]!));
    if (!months || months > 1200) continue;
    const tail = text.slice(m.index! + m[0].length, m.index! + m[0].length + 60);
    return { months, index: m.index!, length: m[0].length, fromSigning: /^\s*(?:from|of)\s+(?:the\s+)?(?:date\s+of\s+(?:signing|execution|this)|signing|execution)/i.test(tail) };
  }
  return null;
}

/** The last day of a term of `months` starting on `start`: 1 April + 11 months → 28 February. */
export const termEnd = (start: string, months: number) => addDays(addMonths(start, months), -1);

/** Whole months from start to end ("2026-07-01" → "2026-12-31" is 6), or null if it isn't a whole number. */
export function wholeMonths(start: string, end: string): number | null {
  for (let n = 1; n <= 1200; n++) {
    const next = addMonths(start, n);
    if (addDays(next, -1) === end || next === end) return n;
    if (next > end) return null;
  }
  return null;
}
